import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import {
  money,
  removeCostRule,
  saveCostRule,
  serviceCatalogCostsQuery,
  type CostsPage,
} from '../api/serviceCatalog'
import { Forbidden, isForbidden } from '../ui/Forbidden'
import { toast } from '../ui/notify'
import { Spinner } from '../ui/Spinner'

/**
 * "What it costs" - `Service_catalog::costs()`, macheta
 * `views/version-3/inventoree/service_catalog/costs.php`.
 *
 * Mai multe reguli se aduna. Un serviciu poate purta in acelasi timp o suma
 * fixa lunara si una pe element - asa se si factureaza infrastructura: o taxa
 * de baza pentru ca merge, plus ceva pentru fiecare masina pe care se sprijina.
 *
 * Numarul de elemente vine din ecranul de compunere, deci nimeni nu tine o a
 * doua lista cu ce se taxeaza: legi un server si cifra lunii urmatoare se muta
 * singura.
 */
export function CostsPanel({ page, entryId }: { page: CostsPage; entryId: number }) {
  const client = useQueryClient()
  const [type, setType] = useState(String(page.costTypes[0]?.value ?? 1))
  const [unit, setUnit] = useState('0')
  const [currency, setCurrency] = useState('RON')
  const [period, setPeriod] = useState(String(page.periods[0]?.value ?? 1))
  const [note, setNote] = useState('')

  const refresh = () => {
    void client.invalidateQueries({ queryKey: ['service-catalog'] })
  }

  const save = useMutation({
    mutationFn: () =>
      saveCostRule(entryId, {
        cost_type: type,
        unit_cost: unit,
        currency,
        period,
        note,
      }),
    onSuccess: (out) => {
      toast(out.message, out.status === 'OK' ? 'Saved' : 'Error', out.status === 'OK' ? 'success' : 'error')
      if (out.status === 'OK') {
        setUnit('0')
        setNote('')
        refresh()
      }
    },
  })

  const remove = useMutation({
    mutationFn: (ruleId: number) => removeCostRule(ruleId),
    onSuccess: (out) => {
      toast(out.message, out.status === 'OK' ? 'Removed' : 'Error', out.status === 'OK' ? 'success' : 'error')
      if (out.status === 'OK') refresh()
    },
  })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    save.mutate()
  }

  return (
    <>
      {page.entry.commercial_template_id > 0 && (
        <div className="alert alert-info">
          <i className="fas fa-info-circle"></i> A commercial template is linked, so{' '}
          <strong>the fixed cost comes from there</strong>. The rules below add only what varies.
        </div>
      )}

      {page.rules.length === 0 ? (
        <div className="adm-empty-inline">
          <i className="fas fa-coins"></i>
          No cost defined. The service will not appear in the cost report.
        </div>
      ) : (
        <table className="table table-hover adm-list">
          <thead>
            <tr>
              <th>Charged</th>
              <th className="adm-numeric">Unit</th>
              <th>Every</th>
              <th>Note</th>
              <th className="text-right">&nbsp;</th>
            </tr>
          </thead>
          <tbody>
            {page.rules.map((rule) => (
              <tr key={rule.ID}>
                <td>{rule.cost_type_name}</td>
                <td className="adm-numeric">
                  {money(rule.unit_cost)} <span className="adm-muted">{rule.currency}</span>
                </td>
                <td>{rule.period_name}</td>
                <td className="adm-muted">{rule.note}</td>
                <td className="text-right">
                  {page.canWrite && (
                    <button
                      type="button"
                      className="btn btn-danger btn-xs"
                      disabled={remove.isPending}
                      onClick={() => remove.mutate(rule.ID)}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {page.canWrite && (
        <>
          <hr />

          <form id="costForm" onSubmit={submit}>
            <div className="row">
              <div className="col-md-4 form-group">
                <label htmlFor={`costType-${entryId}`}>Charged</label>
                <select
                  className="form-control input-sm"
                  id={`costType-${entryId}`}
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                >
                  {page.costTypes.map((one) => (
                    <option value={one.value} key={one.value}>
                      {one.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-3 form-group">
                <label htmlFor={`costUnit-${entryId}`}>Unit cost</label>
                <input
                  type="number"
                  className="form-control input-sm"
                  id={`costUnit-${entryId}`}
                  step="0.0001"
                  min="0"
                  value={unit}
                  onChange={(e) => setUnit(e.target.value)}
                />
              </div>

              <div className="col-md-2 form-group">
                <label htmlFor={`costCurrency-${entryId}`}>Currency</label>
                <input
                  type="text"
                  className="form-control input-sm"
                  id={`costCurrency-${entryId}`}
                  maxLength={3}
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                />
              </div>

              <div className="col-md-3 form-group">
                <label htmlFor={`costPeriod-${entryId}`}>Every</label>
                <select
                  className="form-control input-sm"
                  id={`costPeriod-${entryId}`}
                  value={period}
                  onChange={(e) => setPeriod(e.target.value)}
                >
                  {page.periods.map((one) => (
                    <option value={one.value} key={one.value}>
                      {one.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor={`costNote-${entryId}`}>Note</label>
              <input
                type="text"
                className="form-control input-sm"
                id={`costNote-${entryId}`}
                maxLength={190}
                placeholder="What this line covers"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>

            <button type="submit" className="btn btn-primary btn-sm" disabled={save.isPending}>
              <i className="fas fa-plus"></i> Add
            </button>

            <span className="text-muted small" style={{ marginLeft: 10 }}>
              A yearly figure counts as a twelfth in any one month.
            </span>
          </form>
        </>
      )}
    </>
  )
}

/** Panoul cerut singur - din fereastra listei sau din randul desfacut. */
export function CostsTab({ entryId }: { entryId: number }) {
  const page = useQuery(serviceCatalogCostsQuery(entryId))

  if (isForbidden(page.error)) return <Forbidden />
  if (!page.data) return <Spinner />

  return <CostsPanel page={page.data} entryId={entryId} />
}
